export interface Payload {
    [key: string]: any;
}

export enum LogLevel {
    debug = "debug",
    info = "info",
    notice = "notice",
    warning = "warning",
    error = "error"
}

export interface AppLogEntry {
    data: Payload;
    level: LogLevel;
    message: string;
    tag?: string;
    app_timestamp_ms: number;
}

export interface AppLogConfig {
    logLevel: LogLevel;
    appVersion: string;
    url: string;
    disabled?: boolean;
    defaultPayload?: Payload;
    defaultOptions?: LogOptions;
    sessionId?: string;
    deviceId?: string;
    language?: string;
    errorFilter?: (error: Error) => boolean;
}

export interface LogOptions {
    tag?: string;
}

export interface AppLog {
    debug(message: string, data?: Payload, options?: LogOptions): void;

    info(message: string, data?: Payload, options?: LogOptions): void;

    notice(message: string, data?: Payload, options?: LogOptions): void;

    warning(message: string, data?: Payload, options?: LogOptions): void;

    error(message: string, data?: Payload, options?: LogOptions): void;

    exception(e: Error): void;

    set<Key extends keyof AppLogConfig>(key: Key, value: AppLogConfig[Key]): void;
}

export interface ExtendedWindow extends Window {
    Bolt?: {
        AppLog?: {
            instance: AppLog,
            onload?: (instance: AppLog) => void;
        }
    }
}


interface FunctionInvocations {
    functionName: keyof AppLog,
    arguments: any[];
}

const invocationsQueue: FunctionInvocations[] = [];
function createPassthroughFunction<T extends keyof AppLog>(name: T) {
    return (...args: Parameters<AppLog[T]>) => {
        const w = window as ExtendedWindow;
        if (w?.Bolt?.AppLog?.instance) {
            // @ts-ignore
            return w.Bolt.AppLog.instance[name](...args);
        }  else {
            invocationsQueue.push({
                functionName: name,
                arguments: args
            });
        }
    }
}

window.addEventListener('AppLogLoaded', () => {
    invocationsQueue.forEach((element) => {
        const w = window as ExtendedWindow;
        // @ts-ignore
        w.Bolt?.AppLog?.instance[element.functionName](...element.arguments);
    });
}, false);

const appLogPassthrough = {
    debug: createPassthroughFunction("debug"),
    info: createPassthroughFunction("info"),
    notice: createPassthroughFunction("notice"),
    error: createPassthroughFunction("error"),
    exception: createPassthroughFunction("exception"),
    warning: createPassthroughFunction("warning"),
    set: createPassthroughFunction("set")
};

export function getAppLog(): AppLog {
    const w = window as ExtendedWindow;
    if (!w?.Bolt?.AppLog?.instance) {
        // This is needed in case AppLog is not loaded into the bundle or global scope!
        return appLogPassthrough;
    }

    return w.Bolt.AppLog.instance;
}
